import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Book, Search, ChevronDown, ChevronRight, ExternalLink, Eye, Droplets, Sun, Baby, Scissors, Activity, FileText, Info, CheckCircle, MessageCircle, Send, X, Bot, User, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categorie di patologie con icone
const CATEGORIE = [
  { id: 'strabismo_pediatrica', nome: 'Strabismo / Oftalmologia Pediatrica', icon: Baby, color: '#9C27B0', emoji: '👁️‍🗨️' },
  { id: 'occhio_secco', nome: 'Occhio Secco / Superficie Oculare', icon: Droplets, color: '#2196F3', emoji: '💧' },
  { id: 'cheratocono_cornea', nome: 'Cheratocono / Trapianti di Cornea', icon: Eye, color: '#00BCD4', emoji: '👁️' },
  { id: 'cataratta_refrattiva', nome: 'Cataratta / Chirurgia Refrattiva', icon: Scissors, color: '#FF9800', emoji: '👁️' },
  { id: 'glaucoma', nome: 'Glaucoma', icon: Activity, color: '#F44336', emoji: '👁️' },
  { id: 'retina', nome: 'Retina / Maculopatie', icon: Sun, color: '#4CAF50', emoji: '👁️' },
];

// Mapping patologie -> società
const SOCIETA_INFO = {
  'SIOPS': { nome: 'Società Italiana Oftalmologia Pediatrica e Strabismo', url: 'https://www.siops.it', color: '#9C27B0' },
  'SICCSO': { nome: 'Società Italiana Cellule Staminali e Superficie Oculare', url: 'https://www.siccso.it', color: '#00BCD4' },
  'SIDESO': { nome: 'Società Italiana Dry Eye Study', url: 'https://www.sideso.it', color: '#2196F3' },
  'AICCER': { nome: 'Associazione Italiana Chirurgia Cataratta e Refrattiva', url: 'https://www.aiccer.it', color: '#FF9800' },
  'SIG': { nome: 'Società Italiana Glaucoma', url: 'https://www.sigla.org', color: '#F44336' },
  'FLORETINA': { nome: 'Associazione Medici Retina', url: 'https://www.floretina.it', color: '#4CAF50' },
  'SISO': { nome: 'Società Italiana di Scienze Oftalmologiche', url: 'https://www.soiweb.com', color: '#607D8B' },
  'AIMO': { nome: 'Associazione Italiana Medici Oculisti', url: 'https://www.aimooculisti.it', color: '#795548' },
  'SISO': { nome: 'Società Italiana Scienze Oftalmologiche', url: 'https://www.siso.it', color: '#9E9E9E' },
};

export const LineeGuida = () => {
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  const [selectedPatologia, setSelectedPatologia] = useState(null);
  const [lineeGuidaData, setLineeGuidaData] = useState(null);
  const [societaData, setSocietaData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { role: 'assistant', content: 'Ciao! Sono l\'assistente AI di PRIME VISION specializzato in oftalmologia. 👁️\n\nPosso aiutarti con:\n• Linee guida cliniche\n• Terapie e protocolli\n• Tecniche chirurgiche\n• Diagnosi differenziale\n\nCosa vorresti sapere?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Fetch initial data
  useEffect(() => {
    fetchSocietaData();
  }, []);

  const fetchSocietaData = async () => {
    try {
      const response = await axios.get(`${API}/societa-scientifiche`);
      setSocietaData(response.data);
    } catch (error) {
      console.error('Error fetching societa:', error);
    }
  };

  const fetchLineeGuida = async (patologia) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/linee-guida/${patologia}`);
      setLineeGuidaData(response.data);
    } catch (error) {
      console.error('Error fetching linee guida:', error);
    }
    setLoading(false);
  };

  const handlePatologiaSelect = (patologia) => {
    setSelectedPatologia(patologia);
    fetchLineeGuida(patologia);
  };

  // Scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Send message to AI chatbot
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/oftalmologia-chat`, {
        message: userMessage,
        session_id: sessionId,
        context: 'linee_guida'
      });
      
      setSessionId(response.data.session_id);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        sources: response.data.sources,
        suggested: response.data.suggested_questions
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi scuso, si è verificato un errore. Riprova tra qualche istante.',
        isError: true
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatInputRef.current?.focus(), 100);
    }
  };

  // Handle suggested question click
  const handleSuggestedQuestion = (question) => {
    setChatInput(question);
    setTimeout(() => sendChatMessage(), 100);
  };

  // Clear chat
  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/oftalmologia-chat/${sessionId}`);
      } catch (e) {}
    }
    setSessionId(null);
    setChatMessages([
      { role: 'assistant', content: 'Chat resettata. Come posso aiutarti?' }
    ]);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Render nested object as expandable sections
  const renderNestedContent = (data, parentKey = '', depth = 0) => {
    if (!data || typeof data !== 'object') {
      return <span className="text-[#2A9FCB]">{String(data)}</span>;
    }

    if (Array.isArray(data)) {
      return (
        <ul className="list-disc list-inside space-y-1 ml-4">
          {data.map((item, idx) => (
            <li key={idx} className="text-[#2A9FCB] text-sm">{typeof item === 'object' ? JSON.stringify(item) : item}</li>
          ))}
        </ul>
      );
    }

    return (
      <div className={`space-y-3 ${depth > 0 ? 'ml-4 pl-4 border-l-2 border-gray-200' : ''}`}>
        {Object.entries(data).map(([key, value]) => {
          // Skip metadata fields
          if (['titolo', 'societa_riferimento', 'ultimo_aggiornamento', 'fonti'].includes(key)) return null;
          
          const sectionKey = `${parentKey}_${key}`;
          const isExpanded = expandedSections[sectionKey] !== false; // Default to expanded
          const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
          const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          return (
            <div key={key} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                style={{ borderLeft: `4px solid ${depth === 0 ? '#2A9FCB' : '#2A9FCB'}` }}
              >
                <span className={`font-semibold ${depth === 0 ? 'text-lg' : 'text-base'}`} style={{ color: '#2A9FCB' }}>
                  {formattedKey}
                </span>
                {isObject ? (
                  isExpanded ? <ChevronDown className="w-5 h-5 text-[#2A9FCB]/60" /> : <ChevronRight className="w-5 h-5 text-[#2A9FCB]/60" />
                ) : null}
              </button>
              
              {(isExpanded || !isObject) && (
                <div className="px-4 pb-4">
                  {isObject ? (
                    renderNestedContent(value, sectionKey, depth + 1)
                  ) : Array.isArray(value) ? (
                    <ul className="list-disc list-inside space-y-1 mt-2">
                      {value.map((item, idx) => (
                        <li key={idx} className="text-[#2A9FCB]/80 text-sm">{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[#2A9FCB]/80 mt-2">{value}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render clinical guidelines in structured format
  const renderClinicalGuidelines = (data) => {
    if (!data || !data.linee_guida) return null;

    const lg = data.linee_guida;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-50 to-sky-50 rounded-xl p-6 border border-green-200">
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#2A9FCB' }}>
            {lg.titolo || data.patologia}
          </h2>
          <div className="flex flex-wrap gap-4 text-sm">
            {lg.ultimo_aggiornamento && (
              <span className="bg-white px-3 py-1 rounded-full  text-[#2A9FCB]/80">
                📅 Aggiornamento: {lg.ultimo_aggiornamento}
              </span>
            )}
            {lg.societa_riferimento && (
              <div className="flex gap-2">
                {lg.societa_riferimento.map(soc => (
                  <span key={soc} className="px-3 py-1 rounded-full  text-white text-xs font-medium"
                    style={{ background: SOCIETA_INFO[soc]?.color || '#607D8B' }}>
                    {soc}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {renderNestedContent(lg)}

        {/* Sources */}
        {lg.fonti && lg.fonti.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
              <FileText className="w-5 h-5" />
              Fonti e Riferimenti
            </h3>
            <ul className="space-y-2">
              {lg.fonti.map((fonte, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-[#2A9FCB]/80">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {fonte}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Filter categories based on search
  const filteredCategorie = CATEGORIE.filter(cat =>
    cat.nome.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(160deg, #eaf5fc 0%, #f7fbfe 50%, #e1eef8 100%)' }}>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 px-4" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #fff 50%, rgba(90, 155, 130, 0.1) 100%)' }}>
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-widest uppercase mb-4" style={{ color: '#2A9FCB' }}>
            Per i Medici
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: '#2A9FCB' }}>
            Linee Guida Oftalmologiche
          </h1>
          <p className="text-lg text-[#2A9FCB]/80 max-w-2xl mx-auto">
            Consultazione rapida delle raccomandazioni cliniche dalle principali società scientifiche italiane ed europee
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-6 px-4 -mt-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#2A9FCB]/60" />
            <input
              type="text"
              placeholder="Cerca patologia o linea guida..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 focus:outline-none shadow-lg bg-white"
              style={{ borderColor: '#2A9FCB' }}
              data-testid="search-linee-guida"
            />
          </div>
        </div>
      </section>

      {/* Banner Protocolli Strabismo */}
      <section className="py-4 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/protocolli-strabismo"
            className="block bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-5 text-white hover:from-purple-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            data-testid="banner-protocolli-strabismo"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Baby className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Protocolli Strabismo e Ambliopia</h3>
                  <p className="text-purple-100 text-sm">Screening pediatrico, patching, atropina, chirurgia</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6" />
            </div>
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8">
            
            {/* Sidebar - Categorie */}
            <div className="lg:col-span-4">
              <div className="sticky top-24 space-y-4">
                <h3 className="font-semibold text-lg mb-4" style={{ color: '#2A9FCB' }}>
                  Categorie Patologie
                </h3>
                
                {filteredCategorie.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategoria(selectedCategoria === cat.id ? null : cat.id);
                      setSelectedPatologia(null);
                      setLineeGuidaData(null);
                    }}
                    className={`w-full p-4 rounded-xl text-left transition-all hover:shadow-lg ${
                      selectedCategoria === cat.id ? 'ring-2 shadow-md' : 'bg-white'
                    }`}
                    style={{
                      background: selectedCategoria === cat.id ? `${cat.color}10` : 'white',
                      ringColor: cat.color,
                      border: `1px solid ${selectedCategoria === cat.id ? cat.color : '#E5E7EB'}`
                    }}
                    data-testid={`categoria-${cat.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${cat.color}20` }}>
                        <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div>
                        <span className="text-2xl mr-2">{cat.emoji}</span>
                        <span className="font-medium text-[#2A9FCB]">{cat.nome}</span>
                      </div>
                    </div>
                  </button>
                ))}

                {/* Quick Links - Società */}
                <div className="mt-8 p-4 bg-white rounded-xl border border-gray-200">
                  <h4 className="font-semibold mb-3 text-sm text-[#2A9FCB]/70 uppercase tracking-wide">
                    Società Scientifiche
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(SOCIETA_INFO).slice(0, 6).map(([sigla, info]) => (
                      <a
                        key={sigla}
                        href={info.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-[#2A9FCB]/80 hover:text-gray-900 py-1"
                      >
                        <span className="w-2 h-2 rounded-full" style={{ background: info.color }}></span>
                        <span className="font-medium">{sigla}</span>
                        <ExternalLink className="w-3 h-3 ml-auto" />
                      </a>
                    ))}
                  </div>
                </div>

                {/* Core Essentials */}
                <div className="p-4 bg-gradient-to-br from-green-50 to-sky-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#2A9FCB' }}>
                    🎯 Core Essentials
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Cataratta moderna:</span>
                      <span className="text-[#2A9FCB]/80">AICCER + SICCSO</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Occhio secco:</span>
                      <span className="text-[#2A9FCB]/80">SICCSO + SIDESO</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Strabismo:</span>
                      <span className="text-[#2A9FCB]/80">SIOPS</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-8">
              {!selectedCategoria ? (
                // No category selected - show overview
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <div className="text-center py-12">
                    <Book className="w-16 h-16 mx-auto mb-4" style={{ color: '#2A9FCB' }} />
                    <h3 className="text-xl font-semibold mb-2" style={{ color: '#2A9FCB' }}>
                      Seleziona una Categoria
                    </h3>
                    <p className="text-[#2A9FCB]/70">
                      Scegli una categoria dalla sidebar per visualizzare le linee guida disponibili
                    </p>
                  </div>

                  {/* Quick stats */}
                  <div className="grid grid-cols-3 gap-4 mt-8">
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <p className="text-3xl font-bold" style={{ color: '#2A9FCB' }}>9</p>
                      <p className="text-sm text-[#2A9FCB]/80">Società Scientifiche</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <p className="text-3xl font-bold text-blue-600">6</p>
                      <p className="text-sm text-[#2A9FCB]/80">Categorie Patologie</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-xl">
                      <p className="text-3xl font-bold text-purple-600">2024</p>
                      <p className="text-sm text-[#2A9FCB]/80">Ultimo Aggiornamento</p>
                    </div>
                  </div>
                </div>
              ) : !selectedPatologia ? (
                // Category selected - show patologie list
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-semibold mb-6" style={{ color: '#2A9FCB' }}>
                    {CATEGORIE.find(c => c.id === selectedCategoria)?.nome}
                  </h3>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {selectedCategoria === 'strabismo_pediatrica' && (
                      <>
                        <PatologiaCard 
                          nome="Strabismo"
                          descrizione="Esotropia, exotropia, timing chirurgico, ambliopia"
                          onClick={() => handlePatologiaSelect('strabismo')}
                        />
                        <PatologiaCard 
                          nome="Strabismi Restrittivi"
                          descrizione="Sindrome di Duane, Brown, Möbius, strabismo tiroideo"
                          onClick={() => handlePatologiaSelect('strabismi_restrittivi')}
                        />
                        <PatologiaCard 
                          nome="Paralisi Nervi Cranici"
                          descrizione="Paralisi III, IV, VI nc - Tecnica Nishida, Jensen"
                          onClick={() => handlePatologiaSelect('paralisi_nc')}
                        />
                      </>
                    )}
                    {selectedCategoria === 'occhio_secco' && (
                      <>
                        <PatologiaCard 
                          nome="Occhio Secco"
                          descrizione="Classificazione DEWS II, trattamento stepwise, MGD"
                          onClick={() => handlePatologiaSelect('occhio_secco')}
                        />
                      </>
                    )}
                    {selectedCategoria === 'cheratocono_cornea' && (
                      <>
                        <PatologiaCard 
                          nome="Cheratocono"
                          descrizione="Cross-linking, classificazione, opzioni chirurgiche"
                          onClick={() => handlePatologiaSelect('cheratocono')}
                        />
                      </>
                    )}
                    {selectedCategoria === 'cataratta_refrattiva' && (
                      <>
                        <PatologiaCard 
                          nome="Cataratta"
                          descrizione="IOL premium, femtolaser, indicazioni chirurgiche"
                          onClick={() => handlePatologiaSelect('cataratta')}
                        />
                        <PatologiaCard 
                          nome="Chirurgia Refrattiva"
                          descrizione="LASIK, PRK, SMILE, ICL - indicazioni e work-up"
                          onClick={() => handlePatologiaSelect('chirurgia_refrattiva')}
                        />
                      </>
                    )}
                    {selectedCategoria === 'glaucoma' && (
                      <>
                        <PatologiaCard 
                          nome="Glaucoma"
                          descrizione="Angolo aperto, angolo chiuso, target IOP, MIGS"
                          onClick={() => handlePatologiaSelect('glaucoma')}
                        />
                      </>
                    )}
                    {selectedCategoria === 'retina' && (
                      <>
                        <PatologiaCard 
                          nome="Maculopatia"
                          descrizione="AMD secca/umida, anti-VEGF, edema maculare diabetico"
                          onClick={() => handlePatologiaSelect('maculopatia')}
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : (
                // Patologia selected - show linee guida
                <div>
                  <button
                    onClick={() => {
                      setSelectedPatologia(null);
                      setLineeGuidaData(null);
                    }}
                    className="mb-4 flex items-center gap-2 text-[#2A9FCB]/80 hover:text-gray-900"
                  >
                    ← Torna alle patologie
                  </button>

                  {loading ? (
                    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
                      <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-[#2A9FCB]/70">Caricamento linee guida...</p>
                    </div>
                  ) : lineeGuidaData ? (
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                      {renderClinicalGuidelines(lineeGuidaData)}
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
                      <Info className="w-12 h-12 mx-auto mb-4 text-[#2A9FCB]/60" />
                      <p className="text-[#2A9FCB]/70">Nessuna linea guida trovata per questa patologia</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-blue-800 mb-2">Nota per i Medici</h4>
                <p className="text-sm text-blue-700">
                  Queste linee guida sono estratte dalle principali società scientifiche oftalmologiche italiane ed europee.
                  Si consiglia di consultare sempre le fonti originali per le versioni più aggiornate e complete.
                  Le raccomandazioni terapeutiche vanno sempre personalizzate in base al quadro clinico del singolo paziente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Chatbot Floating Button */}
      <button
        onClick={() => setChatOpen(true)}
        className={`fixed bottom-6 right-6 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center z-40 transition-all hover:scale-110 ${chatOpen ? 'hidden' : ''}`}
        style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}
      >
        <MessageCircle className="w-7 h-7 text-white" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
          <Sparkles className="w-3 h-3 text-white" />
        </span>
      </button>

      {/* AI Chatbot Panel */}
      {chatOpen && (
        <div className="fixed bottom-6 right-6 w-[420px] h-[600px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
          {/* Chat Header */}
          <div className="p-4 border-b flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Assistente AI Oftalmologico</h3>
                <p className="text-xs text-white/80">Powered by GPT-4o</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Nuova conversazione"
              >
                <FileText className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setChatOpen(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-[#2A9FCB]' : 'bg-gradient-to-br from-[#2A9FCB] to-[#2A9FCB]'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={`rounded-2xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-[#2A9FCB] text-white rounded-tr-sm' 
                        : msg.isError 
                          ? 'bg-red-100 text-red-700 rounded-tl-sm'
                          : 'bg-white shadow-sm rounded-tl-sm text-gray-800'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      
                      {/* Sources */}
                      {msg.sources && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            📚 Fonti: {msg.sources.slice(0, 3).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Suggested questions */}
                  {msg.suggested && msg.suggested.length > 0 && idx === chatMessages.length - 1 && (
                    <div className="mt-3 ml-10 space-y-2">
                      <p className="text-xs text-gray-500 font-medium">Domande correlate:</p>
                      {msg.suggested.slice(0, 3).map((q, i) => (
                        <button
                          key={i}
                          onClick={() => handleSuggestedQuestion(q)}
                          className="block w-full text-left text-xs px-3 py-2 bg-green-50 hover:bg-green-100 rounded-lg text-green-700 transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                  <span className="text-sm text-gray-500">Sto elaborando...</span>
                </div>
              </div>
            )}
            
            <div ref={chatEndRef} />
          </div>

          {/* Quick Questions */}
          {chatMessages.length <= 2 && (
            <div className="px-4 py-2 border-t bg-white">
              <p className="text-xs text-gray-500 mb-2">Domande frequenti:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Tecnica Nishida paralisi VI nc',
                  'Sindrome di Duane tipi',
                  'Terapia glaucoma',
                  'Cross-linking cheratocono'
                ].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setChatInput(q); }}
                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-green-100 rounded-full text-gray-700 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Chat Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <input
                ref={chatInputRef}
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Scrivi una domanda..."
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-green-400 text-sm"
                disabled={chatLoading}
              />
              <button
                onClick={sendChatMessage}
                disabled={chatLoading || !chatInput.trim()}
                className="px-4 py-3 rounded-xl text-white font-medium disabled:opacity-50 transition-all hover:shadow-lg"
                style={{ background: '#2A9FCB' }}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// Patologia Card Component
const PatologiaCard = ({ nome, descrizione, onClick }) => (
  <button
    onClick={onClick}
    className="p-6 rounded-xl text-left bg-gray-50 hover:bg-green-50 transition-all hover:shadow-md border border-gray-200 hover:border-green-300"
    data-testid={`patologia-${nome.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <h4 className="font-semibold text-lg mb-2" style={{ color: '#2A9FCB' }}>{nome}</h4>
    <p className="text-sm text-[#2A9FCB]/70">{descrizione}</p>
    <div className="mt-3 flex items-center text-sm font-medium" style={{ color: '#2A9FCB' }}>
      Consulta linee guida <ChevronRight className="w-4 h-4 ml-1" />
    </div>
  </button>
);

export default LineeGuida;
